/**
 * Republic Atlas - UX Mode Types
 * Defines types for Standard/Pro view switching
 */

/**
 * UX viewing modes
 * - standard: Clean, low-complexity view for citizens and casual users
 * - pro: Dense data view for analysts, journalists, campaigns
 */
export type UXMode = 'standard' | 'pro';

/**
 * Content types for NLP analysis
 */
export enum ContentType {
  PRESS_RELEASE = 'press_release',
  SPEECH = 'speech',
  SOCIAL_MEDIA = 'social_media',
  DEBATE = 'debate',
  INTERVIEW = 'interview',
  POLICY_PAPER = 'policy_paper',
  AD = 'advertisement',
  WEBSITE = 'website',
  OTHER = 'other',
}

/**
 * Sentiment scores for content analysis
 */
export enum SentimentScore {
  VERY_NEGATIVE = 'very_negative',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
  POSITIVE = 'positive',
  VERY_POSITIVE = 'very_positive',
}

/**
 * Topic categories for political content
 */
export enum TopicCategory {
  ECONOMY = 'economy',
  HEALTHCARE = 'healthcare',
  IMMIGRATION = 'immigration',
  CLIMATE = 'climate',
  EDUCATION = 'education',
  FOREIGN_POLICY = 'foreign_policy',
  CRIMINAL_JUSTICE = 'criminal_justice',
  GUN_RIGHTS = 'gun_rights',
  ABORTION = 'abortion',
  VOTING_RIGHTS = 'voting_rights',
  TAXES = 'taxes',
  INFRASTRUCTURE = 'infrastructure',
  SOCIAL_SECURITY = 'social_security',
  DEFENSE = 'defense',
  OTHER = 'other',
}

/**
 * Subscription tiers
 */
export type SubscriptionTier = 'free' | 'premium' | 'pro';

/**
 * Feature flags for Pro features
 */
export interface ProFeatures {
  /** Access to advanced filters */
  advancedFilters: boolean;
  /** Access to data exports (CSV, JSON) */
  dataExports: boolean;
  /** Access to historical data beyond 30 days */
  fullHistory: boolean;
  /** Access to comparison tools */
  comparisonTools: boolean;
  /** Access to raw data tables */
  rawDataTables: boolean;
  /** Access to saved views/watchlists */
  savedViews: boolean;
  /** Real-time data updates (vs delayed for free) */
  realTimeUpdates: boolean;
  /** Access to API */
  apiAccess: boolean;
  /** Custom alerts */
  customAlerts: boolean;
  /** Priority support */
  prioritySupport: boolean;
}

/**
 * Default feature access by tier
 */
export const TIER_FEATURES: Record<SubscriptionTier, ProFeatures> = {
  free: {
    advancedFilters: false,
    dataExports: false,
    fullHistory: false,
    comparisonTools: false,
    rawDataTables: false,
    savedViews: false, // Limited to 3 items
    realTimeUpdates: false, // 15-min delay
    apiAccess: false,
    customAlerts: false,
    prioritySupport: false,
  },
  premium: {
    advancedFilters: true,
    dataExports: true, // CSV only
    fullHistory: false, // 30 days only
    comparisonTools: true,
    rawDataTables: false,
    savedViews: false, // Limited
    realTimeUpdates: true,
    apiAccess: false,
    customAlerts: true, // Email only
    prioritySupport: false,
  },
  pro: {
    advancedFilters: true,
    dataExports: true, // CSV + JSON
    fullHistory: true,
    comparisonTools: true,
    rawDataTables: true,
    savedViews: true,
    realTimeUpdates: true,
    apiAccess: true,
    customAlerts: true, // Email + Push
    prioritySupport: true,
  },
};

/**
 * KPI display configuration by UX mode
 */
export interface KPIConfig {
  /** Maximum KPIs to show in snapshot section */
  maxSnapshotKPIs: number;
  /** Show trend indicators */
  showTrends: boolean;
  /** Show comparison to previous period */
  showComparison: boolean;
  /** Show confidence intervals */
  showConfidence: boolean;
}

export const UX_MODE_CONFIG: Record<UXMode, KPIConfig> = {
  standard: {
    maxSnapshotKPIs: 4,
    showTrends: true,
    showComparison: false,
    showConfidence: false,
  },
  pro: {
    maxSnapshotKPIs: 6,
    showTrends: true,
    showComparison: true,
    showConfidence: true,
  },
};

/**
 * Dashboard section visibility by UX mode
 */
export interface DashboardSections {
  snapshotKPIs: boolean;
  trendCharts: boolean;
  geographyMap: boolean;
  historicalData: boolean;
  rawDataTables: boolean;
  advancedFilters: boolean;
  exportControls: boolean;
  comparisonPanel: boolean;
}

export const DASHBOARD_SECTIONS: Record<UXMode, DashboardSections> = {
  standard: {
    snapshotKPIs: true,
    trendCharts: true,
    geographyMap: true,
    historicalData: false, // Hidden behind "View full details"
    rawDataTables: false,
    advancedFilters: false,
    exportControls: false,
    comparisonPanel: false,
  },
  pro: {
    snapshotKPIs: true,
    trendCharts: true,
    geographyMap: true,
    historicalData: true,
    rawDataTables: true,
    advancedFilters: true,
    exportControls: true,
    comparisonPanel: true,
  },
};

/**
 * Data freshness configuration
 */
export interface DataFreshnessConfig {
  /** Delay in minutes for polling data */
  pollingDelayMinutes: number;
  /** Delay in minutes for market data */
  marketDelayMinutes: number;
  /** Auto-refresh interval in seconds (0 = disabled) */
  autoRefreshSeconds: number;
}

export const DATA_FRESHNESS: Record<SubscriptionTier, DataFreshnessConfig> = {
  free: {
    pollingDelayMinutes: 15,
    marketDelayMinutes: 15,
    autoRefreshSeconds: 0,
  },
  premium: {
    pollingDelayMinutes: 0,
    marketDelayMinutes: 0,
    autoRefreshSeconds: 60,
  },
  pro: {
    pollingDelayMinutes: 0,
    marketDelayMinutes: 0,
    autoRefreshSeconds: 30,
  },
};

